import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

// 📋 LISTE CENTRALISÉE DES ICÔNES PAR CATÉGORIE
// Modifier ici pour ajouter/retirer des icônes dans toute l'application

export const ICON_CATEGORIES = {
  // 🏢 Entreprise & Bâtiments
  entreprise: {
    label: '🏢 Entreprise',
    items: ['Building2', 'Building', 'Home', 'Store', 'Briefcase', 'Factory']
  },
  
  // 💻 Technologie & Hardware
  technologie: {
    label: '💻 Technologie',
    items: ['Server', 'HardDrive', 'Database', 'Cpu', 'Monitor', 'Laptop', 'Smartphone', 'Tablet']
  },
  
  // 🌐 Internet & Réseau
  internet: {
    label: '🌐 Internet',
    items: ['Globe', 'Globe2', 'Wifi', 'Cloud', 'CloudCog', 'Network', 'Radio']
  },
  
  // 🔧 Outils & Paramètres
  outils: {
    label: '🔧 Outils',
    items: ['Wrench', 'Hammer', 'Settings', 'Cog', 'Sliders']
  },
  
  // 💻 Code & Développement
  code: {
    label: '💻 Code',
    items: ['Code', 'Code2', 'Terminal', 'Brackets', 'FileCode', 'GitBranch', 'GitCommit']
  },
  
  // ⚡ Énergie & Innovation
  energie: {
    label: '⚡ Énergie & Innovation',
    items: ['Zap', 'Sparkles', 'Rocket', 'Lightbulb', 'Flame', 'Star', 'Award', 'Trophy']
  },
  
  // 🎨 Design & Créativité
  design: {
    label: '🎨 Design',
    items: ['Palette', 'Paintbrush', 'Pen', 'Pencil', 'Layers', 'Box', 'Package', 'Boxes']
  },
  
  // 🎨 Créativité
  creativite: {
    label: '🎨 Créativité',
    items: ['Palette', 'Paintbrush', 'Pen', 'Pencil', 'Lightbulb', 'Rocket', 'Wand2']
  },
  
  // 🛡️ Sécurité
  securite: {
    label: '🛡️ Sécurité',
    items: ['Shield', 'ShieldCheck', 'Lock', 'Key', 'Fingerprint', 'Eye', 'Scan']
  },
  
  // ❤️ Passion & Émotion
  passion: {
    label: '❤️ Passion',
    items: ['Heart', 'HeartHandshake', 'Coffee', 'Sparkles', 'Star', 'Zap', 'Flame']
  },
  
  // 📞 Communication
  telephone: {
    label: '📞 Téléphone',
    items: ['Phone', 'PhoneCall', 'Smartphone', 'Headphones', 'Headset']
  },
  
  // ✉️ Email & Messages
  email: {
    label: '✉️ Email',
    items: ['Mail', 'Inbox', 'Send', 'MessageSquare']
  },
  
  // 📍 Localisation
  localisation: {
    label: '📍 Localisation',
    items: ['MapPin', 'Map', 'MapPinned', 'Navigation']
  },
};

// 🎯 PRESETS PRÉDÉFINIS POUR DIFFÉRENTS USAGES
export const ICON_PRESETS = {
  // Pour les icônes de copyright (logo)
  copyrightLogo: [
    ICON_CATEGORIES.entreprise,
    ICON_CATEGORIES.technologie,
    ICON_CATEGORIES.internet,
    ICON_CATEGORIES.outils,
    ICON_CATEGORIES.energie,
    ICON_CATEGORIES.design,
    ICON_CATEGORIES.securite,
  ],
  
  // Pour les icônes de développeur
  copyrightDeveloper: [
    ICON_CATEGORIES.outils,
    ICON_CATEGORIES.code,
    ICON_CATEGORIES.passion,
    ICON_CATEGORIES.creativite,
    ICON_CATEGORIES.technologie,
  ],
  
  // Pour les icônes de contact
  contactPhone: [ICON_CATEGORIES.telephone],
  contactEmail: [ICON_CATEGORIES.email],
  contactLocation: [ICON_CATEGORIES.localisation],
  
  // Toutes les icônes (pour usage général)
  all: Object.values(ICON_CATEGORIES),
};

// 🎨 COMPOSANT SÉLECTEUR D'ICÔNES
interface IconSelectorProps {
  value: string;
  onChange: (value: string) => void;
  preset?: keyof typeof ICON_PRESETS;
  categories?: typeof ICON_CATEGORIES[keyof typeof ICON_CATEGORIES][];
  placeholder?: string;
  className?: string;
}

export const IconSelector: React.FC<IconSelectorProps> = ({
  value,
  onChange,
  preset,
  categories,
  placeholder = 'Sélectionner une icône',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Utiliser le preset ou les catégories personnalisées
  const options = preset ? ICON_PRESETS[preset] : (categories || ICON_PRESETS.all);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SelectedIcon = (LucideIcons as any)[value];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 pl-10 bg-white/5 border border-white/10 rounded-md text-white text-left flex items-center justify-between hover:bg-white/10 transition-colors"
      >
        <span className="flex items-center gap-2">
          {SelectedIcon && <SelectedIcon className="w-4 h-4 text-gray-400" />}
          <span>{value}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-white/10 rounded-md shadow-xl max-h-80 overflow-y-auto">
          {options.map((group, groupIdx) => (
            <div key={groupIdx}>
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 bg-gray-900/50 sticky top-0">
                {group.label}
              </div>
              {group.items.map((iconName) => {
                const IconComponent = (LucideIcons as any)[iconName];
                const isSelected = value === iconName;
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => {
                      onChange(iconName);
                      setIsOpen(false);
                    }}
                    className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-white/10 transition-colors ${
                      isSelected ? 'bg-white/5 text-white' : 'text-gray-300'
                    }`}
                  >
                    {IconComponent && <IconComponent className="w-4 h-4 flex-shrink-0" />}
                    <span className="flex-1 text-left text-sm">{iconName}</span>
                    {isSelected && <Check className="w-4 h-4 text-blue-400" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IconSelector;
